import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { Search, Building2, Plus, Mail, Phone, MapPin, Briefcase, AlertCircle, Filter, User, X, ArrowRight, ArrowDownAZ, ArrowUpZA, Monitor, HeartPulse, LineChart, ShoppingCart, GraduationCap, Factory, Users, Edit, Trash2 } from "lucide-react";
import OrganizationClient, { OrganizationProfile } from "../data/api/OrganizationClient";
import Loader from "../../../shared/components/Loader";
import Alert from "../../../shared/components/Alert";
import "../organizations.css";

const OrganizationsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [organizations, setOrganizations] = useState<OrganizationProfile[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState(1);
  const [limit] = useState(12);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orgToDelete, setOrgToDelete] = useState<string | null>(null);
  
  // State for slide-over panel
  const [selectedOrganization, setSelectedOrganization] = useState<OrganizationProfile | null>(null);
  const [logoLoaded, setLogoLoaded] = useState(false);

  useEffect(() => {
    if (selectedOrganization) {
      setLogoLoaded(false);
    }
  }, [selectedOrganization]);

  // Open drawer automatically if returned from edit page
  useEffect(() => {
    if (organizations.length > 0 && location.state?.selectedOrgId) {
      const org = organizations.find(o => o.organization_id === location.state.selectedOrgId);
      if (org) {
        setSelectedOrganization(org);
        // Clear state to prevent reopening on refresh
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [organizations, location.state, navigate, location.pathname]);

  const fetchOrganizations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await OrganizationClient.listOrganizations(page, limit, activeSearch, sortOrder);
      setOrganizations(data.records || []);
      setTotalRecords(data.total_no_of_records || 0);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || "Failed to load organizations");
    } finally {
      setLoading(false);
    }
  }, [page, limit, activeSearch, sortOrder]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  const handleSearch = () => {
    setPage(1);
    setActiveSearch(searchTerm);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const clearSearch = () => {
    setSearchTerm("");
    setActiveSearch("");
    setPage(1);
  };

  const toggleSort = () => {
    setSortOrder(prev => prev === "asc" ? "desc" : "asc");
    setPage(1);
  };

  const totalPages = Math.ceil(totalRecords / limit) || 1;

  const handleRowClick = (org: OrganizationProfile) => {
    setSelectedOrganization(org);
  };

  const handleEdit = (e: React.MouseEvent, orgId: string) => {
    e.stopPropagation();
    navigate(`/dashboard/organizations/edit/${orgId}`);
  };

  const handleDelete = (e: React.MouseEvent, orgId: string) => {
    e.stopPropagation();
    setOrgToDelete(orgId);
  };

  const confirmDelete = async () => {
    if (!orgToDelete) return;
    try {
      await OrganizationClient.deleteOrganization(orgToDelete);
      if (selectedOrganization?.organization_id === orgToDelete) {
        setSelectedOrganization(null);
      }
      fetchOrganizations();
    } catch (err) {
      console.error("Failed to delete", err);
    } finally {
      setOrgToDelete(null);
    }
  };

  return (
    <motion.div
      className="learner-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25 }}
    >
      <Loader isLoading={loading} message="Loading organizations..." />

      <div className="lp-hero" style={{ paddingBottom: "24px" }}>
        <div className="lp-hero-left">
          <h1>Organizations</h1>
          <span className="lp-hero-chip">Manage partner companies, departments, and active recruitment pipelines.</span>
        </div>
        <div className="lp-hero-right">
          <button 
            className="btn-primary" 
            onClick={() => navigate("/dashboard/organizations/new")} 
            style={{ borderRadius: "24px", height: 40, padding: "0 20px", display: "flex", alignItems: "center", gap: "8px", fontWeight: 600 }}
          >
            <Plus size={16} /> Add Organization
          </button>
        </div>
      </div>

      <div className="lp-table-panel" style={{ background: "#ffffff", borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 4px 12px rgba(0,0,0,0.02)" }}>
        <div
          className="lp-table-panel-head"
          style={{
            padding: "16px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #e2e8f0",
            background: "#f8fafc",
            flexWrap: "wrap",
            gap: "16px",
            marginBottom: "16px",
            borderTopLeftRadius: "14px",
            borderTopRightRadius: "14px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                padding: "8px 16px",
                gap: "12px",
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "8px",
                  background: "rgba(13, 110, 91, 0.1)",
                  color: "var(--primary)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Building2 size={16} strokeWidth={2.5} />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <div style={{ fontSize: "1rem", color: "#64748b", fontWeight: 600, textTransform: "none", letterSpacing: "0.5px" }}>
                  Total Organizations:
                </div>
                <div
                  style={{
                    fontSize: "1.25rem",
                    fontWeight: 800,
                    color: "#0f172a",
                    lineHeight: 1,
                    letterSpacing: "-0.5px",
                  }}
                >
                  {totalRecords}
                </div>
              </div>
            </div>
          </div>
            
          <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
            <div
              className="lp-search"
              style={{
                height: 40,
                width: 280,
                background: "#fff",
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                position: "relative",
                borderRadius: "24px",
                border: "1px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                padding: "0 12px"
              }}
            >
              <Search size={16} color="#94a3b8" />
              <input
                type="text"
                placeholder="Search organizations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                style={{ flex: 1, paddingLeft: "8px", height: "100%", border: "none", background: "transparent", outline: "none", fontSize: "0.875rem" }}
              />
              {searchTerm && (
                <button
                  style={{
                    border: "none",
                    background: "#f1f5f9",
                    cursor: "pointer",
                    color: "#64748b",
                    width: "20px",
                    height: "20px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 0,
                  }}
                  onClick={clearSearch}
                >
                  <X size={12} />
                </button>
              )}
            </div>
            <button 
              onClick={toggleSort}
              style={{ 
               display: "flex", alignItems: "center", justifyContent: "center",
               width: 40, height: 40, background: "#fff", border: "1px solid #e2e8f0", 
               borderRadius: "50%", color: "#0f172a", cursor: "pointer",
               boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
            }}>
               {sortOrder === "asc" ? <ArrowDownAZ size={18} /> : <ArrowUpZA size={18} />}
            </button>
          </div>
        </div>

        <div className="lp-scroll-container">
          {error && (
            <div
              className="form-error"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "12px",
                background: "#fef2f2",
                color: "#b91c1c",
                borderRadius: "8px",
                marginBottom: "16px",
              }}
            >
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {!loading && organizations.length === 0 ? (
            <div className="table-empty">
              <Building2 size={40} color="#cbd5e1" />
              <span>
                {error
                  ? "Unable to load organizations."
                  : activeSearch
                  ? "No organizations found matching criteria."
                  : "No organizations yet. Click 'Add Organization' to create one."}
              </span>
            </div>
          ) : (
            <>
              <div className="org-grid">
                {organizations.map((org) => {
                  const activeJobs = org.active_jobs || 0;
                  const isZero = activeJobs === 0;
                  return (
                    <div key={org.organization_id} className="org-card" onClick={() => handleRowClick(org)}>
                      <div className="org-card-header">
                        {org.logo_url ? (
                          <img src={org.logo_url} alt={org.name} className="org-card-logo" />
                        ) : (
                          <div className="org-card-logo-placeholder">{getInitials(org.name)}</div>
                        )}
                        <div className={`org-active-jobs-pill ${isZero ? "zero" : ""}`}>
                          <div className="org-active-jobs-dot" />
                          {activeJobs} Active {activeJobs === 1 ? "Job" : "Jobs"}
                        </div>
                      </div>
                      
                      <div className="org-card-body">
                        <h3 className="org-card-title">{org.name}</h3>
                        <div className="org-card-subtitle">
                          {getIndustryIcon(org.industry)}
                          {org.industry || org.company_name || "Department"}
                        </div>
                      </div>
                      
                      <div className="org-card-footer">
                        <div className="org-card-contact">
                          {org.contact?.name ? (
                            <>
                              <User size={14} />
                              <span>{org.contact.name}</span>
                            </>
                          ) : (
                            <>
                              <Mail size={14} />
                              <span>{org.contact?.email || org.email || "No contact info"}</span>
                            </>
                          )}
                        </div>
                        <div className="org-card-view-btn">
                          View <ArrowRight size={14} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {!loading && totalPages > 1 && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "8px", padding: "0 24px 24px 24px" }}>
                  <span style={{ fontSize: "0.875rem", color: "#64748b", fontWeight: 500 }}>
                    Showing {(page - 1) * limit + 1} to {Math.min(page * limit, totalRecords)} of {totalRecords} entries
                  </span>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      style={{
                        padding: "8px 16px",
                        border: "1px solid #e2e8f0",
                        borderRadius: "8px",
                        background: page === 1 ? "#f8fafc" : "#fff",
                        color: page === 1 ? "#94a3b8" : "#0f172a",
                        cursor: page === 1 ? "not-allowed" : "pointer",
                        fontWeight: 600,
                        fontSize: "0.85rem",
                      }}
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPage(Math.min(totalPages, page + 1))}
                      disabled={page === totalPages}
                      style={{
                        padding: "8px 16px",
                        border: "1px solid #e2e8f0",
                        borderRadius: "8px",
                        background: page === totalPages ? "#f8fafc" : "#fff",
                        color: page === totalPages ? "#94a3b8" : "#0f172a",
                        cursor: page === totalPages ? "not-allowed" : "pointer",
                        fontWeight: 600,
                        fontSize: "0.85rem",
                      }}
                    >
                  Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {createPortal(
        <AnimatePresence>
          {selectedOrganization && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setSelectedOrganization(null)}
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0, 0, 0, 0.4)",
                  backdropFilter: "blur(4px)",
                  zIndex: 9998,
                }}
              />
              <motion.div
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "spring", damping: 25, stiffness: 200 }}
                style={{
                  position: "fixed",
                  top: 0,
                  right: 0,
                  bottom: 0,
                  width: "420px",
                  backgroundColor: "#f4f9f7",
                  boxShadow: "-4px 0 24px rgba(0,0,0,0.1)",
                  zIndex: 9999,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div style={{ padding: "24px 24px 20px", background: "#fff", display: "flex", alignItems: "flex-start", justifyContent: "space-between", borderBottom: "1px solid #e2e8f0" }}>
                  <div style={{ display: "flex", gap: "16px", flex: 1 }}>
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: "10px",
                        background: (selectedOrganization.logo_url && logoLoaded) ? "transparent" : "#0f172a",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "1.25rem",
                        fontWeight: 700,
                        boxShadow: (selectedOrganization.logo_url && logoLoaded) ? "none" : "0 4px 12px rgba(15, 23, 42, 0.15)",
                        position: "relative",
                        overflow: "hidden"
                      }}
                    >
                      {selectedOrganization.logo_url && (
                        <img 
                          src={selectedOrganization.logo_url} 
                          alt="Logo" 
                          onLoad={() => setLogoLoaded(true)}
                          style={{ 
                            width: "100%", 
                            height: "100%", 
                            objectFit: "cover", 
                            borderRadius: "10px",
                            opacity: logoLoaded ? 1 : 0,
                            position: logoLoaded ? "relative" : "absolute",
                            zIndex: 2,
                            transition: "opacity 0.3s ease"
                          }} 
                        />
                      )}
                      {(!selectedOrganization.logo_url || !logoLoaded) && (
                        <span style={{ position: "absolute", zIndex: 1 }}>{getInitials(selectedOrganization.name)}</span>
                      )}
                    </div>
                    <div>
                      <h2 style={{ margin: "0 0 4px", fontSize: "1.1rem", color: "#0f172a", fontWeight: 700 }}>{selectedOrganization.name}</h2>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "#64748b", fontSize: "0.8rem", fontWeight: 500 }}>
                        <MapPin size={14} />
                        {selectedOrganization.location || "Location not set"}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginTop: "16px" }}>
                        <button
                          onClick={(e) => selectedOrganization.organization_id && handleEdit(e, selectedOrganization.organization_id)}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                            padding: '8px 16px',
                            background: 'linear-gradient(135deg, #fb923c, #f97316)',
                            color: '#fff', border: 'none', borderRadius: '24px',
                            fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            boxShadow: '0 4px 12px rgba(249, 115, 22, 0.2)'
                          }}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 6px 16px rgba(249, 115, 22, 0.3)';
                            (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)';
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 12px rgba(249, 115, 22, 0.2)';
                            (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)';
                          }}
                        >
                          <Edit size={14} />
                          Edit Details
                        </button>
                        <button
                          onClick={(e) => selectedOrganization.organization_id && handleDelete(e, selectedOrganization.organization_id)}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                            padding: '8px 16px',
                            background: 'linear-gradient(135deg, #f87171, #ef4444)',
                            color: '#fff', border: 'none', borderRadius: '24px',
                            fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)'
                          }}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 6px 16px rgba(239, 68, 68, 0.3)';
                            (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)';
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.2)';
                            (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)';
                          }}
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedOrganization(null)}
                    style={{ background: "transparent", border: "none", cursor: "pointer", color: "#64748b", padding: "4px" }}
                  >
                    <X size={18} />
                  </button>
                </div>

                <div style={{ flex: 1, padding: "24px", overflowY: "auto" }}>
                  <div style={{ marginBottom: "12px", fontSize: "0.75rem", fontWeight: 700, color: "#64748b", letterSpacing: "0.5px", textTransform: "uppercase" }}>
                    Primary Contact
                  </div>
                  {selectedOrganization.contact ? (
                    <div style={{ 
                      background: "#fff", 
                      borderRadius: "16px", 
                      padding: "20px", 
                      border: "1px solid #e2e8f0", 
                      borderLeft: "4px solid #0d6e5b",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.02)"
                    }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                          <div style={{ width: 48, height: 48, borderRadius: "12px", background: "#ccfbf1", color: "#0f766e", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.25rem", fontWeight: 700 }}>
                            {getInitials(selectedOrganization.contact.name || "?")}
                          </div>
                          <div>
                            <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "1.05rem", marginBottom: "2px" }}>
                              {selectedOrganization.contact.name || "Unknown Contact"}
                            </div>
                            <div style={{ color: "#64748b", fontSize: "0.8rem", fontWeight: 500 }}>
                              Point of Contact
                            </div>
                          </div>
                        </div>
                        {selectedOrganization.contact.email && (
                          <a href={`mailto:${selectedOrganization.contact.email}`} style={{ width: 36, height: 36, borderRadius: "50%", background: "#f8fafc", border: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", color: "#0d6e5b", transition: "all 0.2s" }} onMouseOver={(e) => { e.currentTarget.style.background = "#ccfbf1"; e.currentTarget.style.borderColor = "#99f6e4"; }} onMouseOut={(e) => { e.currentTarget.style.background = "#f8fafc"; e.currentTarget.style.borderColor = "#e2e8f0"; }}>
                            <Mail size={16} />
                          </a>
                        )}
                      </div>
                      
                      {selectedOrganization.contact.phone || selectedOrganization.contact.email ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: "12px", background: "#f8fafc", padding: "12px 16px", borderRadius: "12px", border: "1px solid #f1f5f9" }}>
                          {selectedOrganization.contact.phone && (
                            <div style={{ display: "flex", alignItems: "center", gap: "10px", color: "#475569", fontSize: "0.85rem" }}>
                              <Phone size={14} color="#94a3b8" />
                              <span style={{ fontWeight: 500 }}>{selectedOrganization.contact.phone}</span>
                            </div>
                          )}
                          {selectedOrganization.contact.email && (
                            <div style={{ display: "flex", alignItems: "center", gap: "10px", color: "#475569", fontSize: "0.85rem" }}>
                              <Mail size={14} color="#94a3b8" />
                              <span style={{ fontWeight: 500 }}>{selectedOrganization.contact.email}</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", background: "#f8fafc", padding: "12px 16px", borderRadius: "12px", border: "1px dashed #e2e8f0", color: "#94a3b8", fontSize: "0.8rem", fontStyle: "italic" }}>
                          No contact details provided
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ background: "#fff", border: "1px dashed #cbd5e1", borderRadius: "10px", padding: "12px", textAlign: "center", color: "#94a3b8", fontSize: "0.8rem" }}>
                      No primary contact specified
                    </div>
                  )}
                  
                  <div style={{ marginTop: "24px", marginBottom: "8px", fontSize: "0.7rem", fontWeight: 700, color: "#0d6e5b", letterSpacing: "1px", textTransform: "uppercase" }}>
                    Organization Info
                  </div>
                  <div style={{ background: "#fff", borderRadius: "10px", border: "1px solid #e2e8f0", padding: "16px" }}>
                    <div style={{ display: "flex", marginBottom: "16px" }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: "0.7rem", color: "#64748b", marginBottom: "4px" }}>Industry</div>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px", fontWeight: 500, color: "#0f172a", fontSize: "0.875rem" }}>
                          {getIndustryIcon(selectedOrganization.industry)}
                          {selectedOrganization.industry || "—"}
                        </div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: "0.7rem", color: "#64748b", marginBottom: "4px" }}>Email</div>
                        <div style={{ fontWeight: 500, color: "#0f172a", fontSize: "0.875rem", wordBreak: "break-all" }}>{selectedOrganization.email || "—"}</div>
                      </div>
                    </div>
                    <div style={{ display: "flex" }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: "0.7rem", color: "#64748b", marginBottom: "4px" }}>Active Jobs</div>
                        <div style={{ display: "inline-flex", padding: "2px 8px", background: "#f1f5f9", borderRadius: "12px", fontSize: "0.8rem", fontWeight: 700, color: "#334155" }}>
                          {selectedOrganization.active_jobs || 0}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}

      <Alert
        show={!!orgToDelete}
        title="Delete Organization"
        message={`Are you sure you want to delete <b>${organizations.find(o => o.organization_id === orgToDelete)?.name || "this organization"}</b>? This action cannot be undone.`}
        type="warning"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        onPrimaryClick={confirmDelete}
        onSecondaryClick={() => setOrgToDelete(null)}
        onClose={() => setOrgToDelete(null)}
      />
    </motion.div>
  );
};

const getInitials = (name: string) => {
  const parts = (name || "").trim().split(/\s+/);
  const initials = parts
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
  return initials || "?";
};

const getIndustryIcon = (industry?: string | null) => {
  const size = 14;
  switch (industry?.toLowerCase()) {
    case "technology": return <Monitor size={size} />;
    case "healthcare": return <HeartPulse size={size} />;
    case "finance": return <LineChart size={size} />;
    case "retail": return <ShoppingCart size={size} />;
    case "education": return <GraduationCap size={size} />;
    case "manufacturing": return <Factory size={size} />;
    case "consulting": return <Users size={size} />;
    default: return <Building2 size={size} />;
  }
};

export default OrganizationsPage;